import { useState, useMemo } from "react";
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
    ZoomableGroup,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { scaleLinear } from "d3-scale";
import { Box } from "@mui/material";
import {useTranslator} from "../../context/TranslatorContext";
import europeGeoJSON from "../../data/europe.json";

const LdsMap = ({ participantData = [], selectedCountry, onCountryClick }) => {
    const [tooltipContent, setTooltipContent] = useState(null);
    const {messages} = useTranslator();
    // Calculate country data from participant list
    const countryData = useMemo(() => {
        const map = {};
        participantData.forEach(item => {
            const geoName = item.country_label;
            if (!map[geoName]) {
                map[geoName] = {
                    count: 0,
                    label: item.country_label,
                    iri: item.country_of_registration
                };
            }
            map[geoName].count++;
        });
        return map;
    }, [participantData]);

    // console.log(participantData);

    const maxCount = Math.max(
        ...Object.values(countryData).map(c => c.count),
        1
    );
    const colorScale = scaleLinear()
        .domain([0, maxCount])
        .range(["#D1E2F8", "#0F2651"]);

    /* const dataMap = countryData.reduce((acc, item) => {
        acc[item.name] = item.total_number_of_connectors;
        return acc;
    }, {}); */

    return (
        <Box p={2} position="relative" sx={{ width: "100%", height: "100%" }}>
            <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                    scale: 550,
                    center: [15, 52],
                }}
                style={{ width: "100%", height: "100%" }}
            >
                <ZoomableGroup>
                    <Geographies geography={europeGeoJSON}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const countryName = geo.properties.NAME || geo.properties.name;
                                const countryInfo = countryData[countryName];
                                const connectorCount = countryInfo?.count;
                                const translatedLabel = countryInfo?.label || countryName;
                                const hasData = !!countryInfo;
                                const isSelected = selectedCountry === countryName;

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={
                                            isSelected
                                                ? "#3A5D96"
                                                : hasData
                                                    ? colorScale(connectorCount)
                                                    : "#e3e8ef"
                                        }
                                        stroke={isSelected ? "#0F2651" : "#cdd5df"}
                                        strokeWidth={isSelected ? 1.5 : 0.5}
                                        style={{
                                            default: { outline: "none", transition: "all 0.3s ease" },
                                            hover: {
                                                fill: hasData ? "#3A5D96" : "#eef2f6",
                                                outline: "none",
                                                cursor: hasData ? "pointer" : "default",
                                            },
                                            pressed: { outline: "none" },
                                        }}
                                        onClick={() => {
                                            if (hasData) {
                                                onCountryClick && onCountryClick(
                                                    selectedCountry === countryName ? null : countryName
                                                );
                                            }
                                        }}
                                        onMouseEnter={(event) => {
                                            if (hasData) {
                                                const { clientX, clientY } = event;
                                                setTooltipContent({
                                                    text: `${translatedLabel}: ${connectorCount} ${messages.organisation_text}`,
                                                    x: clientX,
                                                    y: clientY
                                                });
                                            }
                                        }}
                                        onMouseLeave={() => setTooltipContent(null)}
                                    />
                                );
                            })
                        }
                    </Geographies>

                    <Geographies geography={europeGeoJSON}>
                        {({ geographies }) =>
                            geographies
                                .filter((geo) => countryData[geo.properties.NAME])
                                .map((geo) => {
                                    const countryName = geo.properties.NAME;
                                    const centroid = geoCentroid(geo);
                                    const isSelected = selectedCountry === countryName;
                                    {/* Markers only for countries that exist in data */}
                                    return (
                                        <Marker key={countryName} coordinates={centroid}>
                                            <g
                                                fill="none"
                                                stroke={isSelected ? "#FF8B63" : "#FF5630"}
                                                strokeWidth={isSelected ? "3" : "2"}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                transform="translate(-12, -24)"
                                                style={{ transition: "all 0.3s ease" }}
                                            >
                                                <circle cx="12" cy="10" r="3" />
                                                <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6.9 8 11.7z" />
                                            </g>
                                        </Marker>
                                    );
                                })
                        }
                    </Geographies>
                </ZoomableGroup>
            </ComposableMap>

            {/* Tooltips */}
            {tooltipContent && (
                <div
                    style={{
                        position: "fixed",
                        top: tooltipContent.y + 15,
                        left: tooltipContent.x + 15,
                        backgroundColor: "#0F2651",
                        color: "white",
                        padding: "6px 10px",
                        borderRadius: "4px",
                        fontSize: "13px",
                        pointerEvents: "none",
                        zIndex: 1000
                    }}
                >
                    {tooltipContent.text}
                </div>
            )}
        </Box>
    );
};

export default LdsMap;
